/**
 * The use cases whose bookings carry an overtime allowance — one more opening
 * past the booked end, then the call centre (#1386).
 *
 * **Mirror of `OVERTIME_USE_CASES` in
 * `backend/src/core/modules/common/rental/overtimeAccess.ts`**, which is the
 * definition; the two deployables cannot import each other, so this is the
 * ADR-0014 shape — the copies name each other and each is pinned by a test
 * carrying the same table. **Change one, change both.**
 *
 * The rule itself does not vary by use case (CEO, 2026-09-14): a SUP gets
 * exactly one overtime opening, so a locker — the easier case — cannot need
 * more. `storage` was missing here, in the backend list, in the door token's
 * lifetime and in `OrdersPage`'s CTA gate, all spelled by hand, which left a
 * storage customer past their end with **no** opening at all.
 */
const OVERTIME_USE_CASES = ["sup", "rental", "storage"];

/** Whether this use case carries an overtime allowance at all. */
export const hasOvertimeAllowance = (
  useCase?: string | null,
): boolean =>
  Boolean(useCase) && OVERTIME_USE_CASES.includes(String(useCase).toLowerCase());

/**
 * Canonical overtime predicate (see
 * `docs/architecture/check-in-check-out-flow.md` → "Usage lifecycle — state
 * model"). Overtime is a *view* of active, not its own stored event: a usage is
 * in overtime when it is active, not yet finished, and the clock has passed the
 * booking's scheduled end. There is **no grace period** in the onset — grace is
 * a fee-only concept handled elsewhere and must not affect status.
 */
export const isOvertime = (
  scheduledEnd: Date | string | null | undefined,
  isActive: boolean,
  isFinished: boolean,
  now: Date = new Date(),
): boolean =>
  Boolean(isActive && !isFinished && scheduledEnd && now > new Date(scheduledEnd));
